import { type CheckpointState, checkpointState } from '@ofmos/shared/checklist';
import { useEffect, useState } from 'react';
import { useAppClient } from '../../data/ClientProvider';
import type { Actor, FieldMetadata, FieldValue, RecordRow } from '../../data/client';
import { useTasks, useUpdateTask } from '../../data/hooks';
import {
  BottomSheet,
  cx,
  Popover,
  StatusSymbol,
  StepMark,
  type StepMarkState,
  Tooltip,
  useIsDesktop,
  useToast,
} from '../../ui';
import { useSession } from '../auth/useSession';
import {
  checkpointDateLabel,
  deadlineSentence,
  sourceBadgeLabel,
  toCheckpointField,
  waiverLabelOf,
} from './checklist';
import { todayKey } from './dueDate';
import type { FieldWriteOutcome } from './useRecordFieldSave';

/**
 * THE CHECKLIST SECTION (`docs/decisions/phase-b-coach.md` D3, D4).
 *
 * A group of dates, rendered as the thing a coach actually reads in the morning: eight rows,
 * a circle each, and one sentence per open row saying when it is due. It is NOT a list of
 * checkboxes and this is the product decision the whole slice rests on (P2): a tick is a DAY.
 * The day is what starts the next deadline, what the source badge is about, and what the
 * history keeps - a boolean would throw all three away the moment somebody ticked it.
 *
 * IT IS CHOSEN BY CAPABILITY, NEVER BY NAME (D3). `RecordDetails` renders this instead of
 * plain rows when every DATE field of a group carries a `checkpoint`, so an operator who
 * declares deadlines on some other object's dates gets the same section without a line of
 * code. `isChecklistGroup` in `checklist.ts` is that test and says why it is all-or-nothing.
 *
 * ONE TAP IS TWO TRUTHS (D4). Marking a checkpoint done writes today's date AND completes
 * every open task on this record whose title is that checkpoint's label - the task a playbook
 * created (B2's "New customer"), which otherwise stays open under a date that says the work
 * happened. The toast says so, because a click that changes two things must not report one.
 *
 * A MANUAL WRITE HUMAN-LOCKS THE FIELD, and that is intended rather than tolerated (D4): the
 * coach who ticks the row is the authority on it, and INV-5 then keeps a feeder from
 * overwriting them. `Clear` is the way back and does both halves in order - clear the value,
 * then release the lock - because a cleared date that no feeder may fill is a hole nobody can
 * close from the outside.
 *
 * BOTH OF THOSE SECOND HALVES ARE GATED ON THE WRITE HAVING LANDED. `save` resolves with the
 * ending it took, and only `applied` means the value on screen is the value in the database -
 * so a refused or queued write closes no task and releases no lock. Getting that wrong is not
 * cosmetic in either case: an unreleased date reported as cleared is a lock nobody can find,
 * and a released lock over a date that is still there is the feeder window the paragraph above
 * exists to prevent.
 */

export interface ChecklistSectionProps {
  readonly objectId: string;
  readonly record: RecordRow;
  /** The group's checkpoint fields, in metadata order (the seed's is chronological). */
  readonly fields: readonly FieldMetadata[];
  /** Newest writer per field, from the timeline the page already loaded (D3). */
  readonly writers: ReadonlyMap<string, Actor>;
  /** D21: an archived record is read-only, so no circle is a control. */
  readonly readOnly: boolean;
  readonly savingFieldId: string | null;
  readonly onSave: (field: FieldMetadata, next: FieldValue) => Promise<FieldWriteOutcome>;
  /** Opens the row's ordinary date editor, which the page owns (one open row at a time). */
  readonly onEdit: (fieldId: string) => void;
  /** Renders the row that is being edited, so the editor stays the page's one editor. */
  readonly renderEditor: (field: FieldMetadata) => React.ReactNode;
  readonly editingFieldId: string | null;
}

export function ChecklistSection({
  objectId,
  record,
  fields,
  writers,
  readOnly,
  savingFieldId,
  onSave,
  onEdit,
  renderEditor,
  editingFieldId,
}: ChecklistSectionProps) {
  const today = todayKey();
  const session = useSession();
  const currentUserId = session.data?.id ?? null;

  return (
    <ul className="divide-y divide-line-subtle">
      {fields.map((field) => {
        if (editingFieldId === field.id && !readOnly) {
          return <li key={field.id}>{renderEditor(field)}</li>;
        }
        return (
          <ChecklistRow
            key={field.id}
            objectId={objectId}
            record={record}
            field={field}
            fields={fields}
            state={checkpointState(toCheckpointField(field), record.values, today)}
            writer={writers.get(field.id)}
            currentUserId={currentUserId}
            readOnly={readOnly}
            saving={savingFieldId === field.id}
            onSave={onSave}
            onEdit={onEdit}
          />
        );
      })}
    </ul>
  );
}

/**
 * THE HEADER'S RIGHT-HAND SIDE (D3): "3 of 8" and one dot per checkpoint.
 *
 * The dots are the section's whole state in the width of a word, so a collapsed group still
 * says whether anything is late. Graphite everywhere except the warning ring, which is
 * DESIGN.md's colour rule applied literally: a filled dot and an open dot are both ordinary,
 * and only "past its deadline" means something a colour should shout.
 *
 * `aria-hidden`, because the count beside them already says it in words and eight unlabelled
 * dots read out one by one are noise. Exported so `RecordDetails` can put it in the
 * disclosure header rather than inside the body it opens.
 */
export function ChecklistHeaderMeta({
  fields,
  values,
}: {
  readonly fields: readonly FieldMetadata[];
  readonly values: Readonly<Record<string, FieldValue>>;
}) {
  const today = todayKey();
  /** A waived checkpoint (finding F6) is not owed: no dot and no count, as on the board. */
  const owed = fields
    .map((field) => ({
      field,
      state: checkpointState(toCheckpointField(field), values, today),
    }))
    .filter((entry) => entry.state.status !== 'waived');
  const done = owed.filter((entry) => entry.state.status === 'done').length;

  return (
    <span className="ml-auto flex items-center gap-2 pl-2">
      <span className="flex items-center gap-1" aria-hidden="true">
        {owed.map(({ field, state }) => (
          <span
            key={field.id}
            className={cx(
              'size-1.5 rounded-full',
              state.status === 'done'
                ? 'bg-ink-muted'
                : state.status === 'late'
                  ? 'border border-warning'
                  : 'border border-line-strong',
            )}
          />
        ))}
      </span>
      <span className="text-2xs tabular-nums text-ink-muted">{`${done} of ${owed.length}`}</span>
    </span>
  );
}

interface ChecklistRowProps {
  readonly objectId: string;
  readonly record: RecordRow;
  readonly field: FieldMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly state: CheckpointState;
  readonly writer: Actor | undefined;
  readonly currentUserId: string | null;
  readonly readOnly: boolean;
  readonly saving: boolean;
  readonly onSave: (field: FieldMetadata, next: FieldValue) => Promise<FieldWriteOutcome>;
  readonly onEdit: (fieldId: string) => void;
}

function ChecklistRow({
  objectId,
  record,
  field,
  fields,
  state,
  writer,
  currentUserId,
  readOnly,
  saving,
  onSave,
  onEdit,
}: ChecklistRowProps) {
  const isDesktop = useIsDesktop();
  const toast = useToast();
  const client = useAppClient();
  const tasks = useTasks(objectId, record.id);
  const updateTask = useUpdateTask(objectId, record.id);
  const [optionsOpen, setOptionsOpen] = useState(false);
  /**
   * A SPINNER THAT ARRIVES AFTER 140ms AND NOT BEFORE. A local write settles in tens of
   * milliseconds, so a spinner on the first frame is a flash the eye reads as a glitch rather
   * than as progress. `--duration-fast` is the same 140 the pointer tier uses, which is the
   * threshold this product already treats as "long enough to be a wait".
   */
  const showSpinner = useDelayed(saving, 140);

  const done = state.status === 'done';
  const blocked = state.status === 'blocked';
  const dateLabel = checkpointDateLabel(record.values[field.id]);
  const sentence = deadlineSentence(state, waiverLabelOf(field, fields));
  const badge = sourceBadgeLabel(writer, currentUserId);
  const blockerLabel = blockerOf(field, fields);

  const markDone = async () => {
    /**
     * NOTHING ELSE HAPPENS UNLESS THE DATE LANDED. A refused write (INV-5) and a queued one
     * (INV-10) both leave the field as it was and have already said so through their own
     * marker and toast; closing the playbook's task on top of either would report work as
     * finished on a record where the date is not there.
     */
    if ((await onSave(field, todayKey())) !== 'applied') return;
    /**
     * THE TASK HALF (D4, fit-gap 5.2). Title equality and not a stored link, because the
     * playbook that creates the task names it after the checkpoint (D10) and nothing on
     * either row points at the other: a link would be a fourth thing to keep in step, and a
     * coach who renames the checkpoint would silently break it either way. Open tasks only -
     * completing a completed task would rewrite whoever finished it.
     *
     * THE LIST IS WAITED FOR RATHER THAN READ OPTIMISTICALLY. `useTasks` is a query like any
     * other, so a tap in the second before it resolves would match an EMPTY list, close
     * nothing, and report "set to today" - the task left open with no sign that anything was
     * missed. `refetch` resolves with the rows whether the query had landed or not, and it is
     * one request on a list this page was fetching anyway.
     */
    const loaded = tasks.isSuccess ? tasks.data : (await tasks.refetch()).data;
    const matches = (loaded ?? []).filter(
      (task) => task.doneAt === null && task.title.trim() === field.label.trim(),
    );
    /**
     * AWAITED, because the toast is a claim about what the SERVER holds. `mutate` returns
     * before the round trip, so the old code said "1 task completed" while the request was
     * still in flight and said it again after one that failed.
     */
    try {
      await Promise.all(
        matches.map((task) => updateTask.mutateAsync({ taskId: task.id, patch: { done: true } })),
      );
    } catch (error) {
      toast({
        title: `${field.label} set to today, but the task could not be closed`,
        description: (error as Error).message,
        tone: 'danger',
      });
      return;
    }
    toast({
      title:
        matches.length > 0
          ? `${field.label} set to today · ${matches.length === 1 ? '1 task completed' : `${matches.length} tasks completed`}`
          : `${field.label} set to today`,
      tone: 'success',
    });
  };

  /**
   * CLEAR IS TWO REQUESTS IN ONE ORDER (D4), and the order is the whole point: the value
   * goes first, then the lock. A release that ran first would leave a window in which a
   * feeder may write and the old date is still there, which is the one outcome "let a feeder
   * fill it again" must not produce.
   *
   * WHICH IS WHY THE SECOND ONE IS CONDITIONAL. A write can end `pending_approval`,
   * `write_blocked` or `failed`, and in all three the old date is still on the record - so
   * releasing the lock anyway would open exactly the window the paragraph above forbids, with
   * a toast claiming the date was cleared on top of it. The release runs only on `applied`,
   * the same test `markDone` makes before it closes a task.
   *
   * If the SECOND fails the date IS cleared, and the toast says exactly that rather than
   * reporting a success or a failure - both would be false.
   */
  const clear = async () => {
    setOptionsOpen(false);
    const outcome = await onSave(field, null);
    if (outcome !== 'applied') {
      /**
       * `save` has already said what happened to the WRITE; what it cannot say is what that
       * means for the thing the person actually pressed, which was a request to hand the
       * field back. That is this line's job.
       */
      toast({
        title:
          outcome === 'pending_approval'
            ? `${field.label} is waiting for approval, so it is still held`
            : `${field.label} was not cleared, so it is still held`,
        description: 'Automated writers cannot fill it again until the date is gone.',
        tone: outcome === 'pending_approval' ? 'neutral' : 'danger',
      });
      return;
    }
    try {
      await client.records.releaseFieldLock(objectId, record.id, field.id);
      toast({ title: `${field.label} was cleared`, tone: 'neutral' });
    } catch (error) {
      toast({
        title: `${field.label} was cleared, but it is still held`,
        description: `Automated writers cannot fill it again yet. ${(error as Error).message}`,
        tone: 'danger',
      });
    }
  };

  const circleLabel = done
    ? `${field.label}, done on ${dateLabel ?? 'an unknown day'}, options`
    : blocked
      ? `${field.label} is not yet possible`
      : `Mark ${field.label} done`;

  /**
   * THE CIRCLE, AND ITS CLICK BELONGS TO WHOEVER WRAPS IT.
   *
   * On a desktop a done circle is a `Popover` trigger, and Radix's `asChild` COMPOSES its
   * own toggle with the child's handler - so a child that also set the open state would
   * fight it and the second click would never close the panel. The wrapper decides: the
   * popover gets a circle with no handler of its own, the sheet gets one that opens it.
   *
   * A BLOCKED CIRCLE IS `aria-disabled`, NOT `disabled`. A disabled button takes no focus
   * and fires no pointer events, so the tooltip that explains WHY it cannot be ticked would
   * be unreachable by keyboard and unreliable by mouse - the explanation would be missing
   * from exactly the state that needs one. It is inert because its handler is absent.
   *
   * THE CIRCLE IS THE ONE STEP MARK at its row size (`ui/StepMark`, BR-PARTS; DESIGN.md B1,
   * B10): it draws the state the matrix cell draws, passes the popover's and the tooltip's props
   * to its button, and puts the delayed spinner where the mark is. Its name stays this row's
   * ("..., options" on a done row, because a click opens Change Date and Clear), and it carries
   * no pressed state, as before.
   */
  const circleButton = (onClick?: (() => void) | undefined) => (
    <StepMark
      size="row"
      state={stepMarkStateOf(state)}
      stepLabel={field.label}
      doneOn={dateLabel}
      busy={showSpinner}
      disabled={readOnly || saving}
      aria-label={circleLabel}
      toggle={false}
      {...(onClick === undefined ? {} : { onClick })}
    />
  );

  return (
    <li className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-0.5 px-4 py-2 md:min-h-10 md:flex-nowrap">
      {blocked ? (
        <Tooltip
          label={
            blockerLabel === null ? 'Not yet possible' : `Not yet possible: needs ${blockerLabel}`
          }
        >
          {circleButton()}
        </Tooltip>
      ) : done && !readOnly ? (
        <SetOptions
          open={optionsOpen}
          onOpenChange={setOptionsOpen}
          isDesktop={isDesktop}
          label={field.label}
          circle={circleButton}
          onChangeDate={() => {
            setOptionsOpen(false);
            onEdit(field.id);
          }}
          onClear={() => void clear()}
        />
      ) : (
        circleButton(done ? undefined : () => void markDone())
      )}

      {/*
        THE LABEL IS THE ROW'S OTHER CONTROL, and it opens the ordinary date editor rather
        than doing anything of its own (D4): a checkpoint is a date field, and the way to
        type a different day into a date field is the way it is everywhere else in the
        product. Ghost styling, because a row of eight underlined links is a page of links.
      */}
      <button
        type="button"
        disabled={readOnly}
        onClick={() => onEdit(field.id)}
        className={cx(
          /**
           * `.hit-expand` FOR THE FLOOR, and it is the finding this round produced: the label
           * is a real button and its box is as tall as its text, so at 375 it measured
           * 259x27 against criterion 1's 40px - eight times over, once per row. The recipe is
           * the Switch's and the Checkbox's: an absolutely positioned pseudo-element grows the
           * PRESSED box to `--touch-target-min` and changes no pixel. The row keeps its 44px,
           * the text keeps its position, and the thumb gets the target it always looked like.
           */
          'hit-expand min-w-0 flex-1 truncate rounded-control py-0.5 text-left text-base text-ink',
          !readOnly && 'hover:text-accent focus-visible:outline-2 focus-visible:outline-accent',
        )}
      >
        {field.label}
      </button>

      {/*
        THE TRAILING META, and at 375 it is the second line: `basis-full` wraps it under the
        label, indented past the circle so the two lines read as one row rather than as two.
      */}
      {/* `pl-8.5` is the circle (5.5) plus the row's gap (3), so the second line starts under
          the label rather than under the circle. On the grid, not in pixels. */}
      <span className="flex basis-full items-center gap-2 pl-8.5 text-2xs md:basis-auto md:justify-end md:pl-0">
        {done ? (
          <>
            {dateLabel !== null && (
              <span className="tabular-nums whitespace-nowrap text-ink-muted">{dateLabel}</span>
            )}
            {/* WAVE 3 (R-B1): the last `Badge` inside the record. Who ticked a checkpoint is
                a fact about the row, not a tag on it, so it is the words in the secondary
                label colour - the same shape `SourceBadge` and the task row now use. */}
            {badge !== null && <span className="text-ink-muted">{badge}</span>}
          </>
        ) : (
          sentence !== null && (
            <span
              className={cx(
                'whitespace-nowrap',
                state.status === 'late'
                  ? 'text-warning'
                  : blocked
                    ? 'text-ink-subtle'
                    : 'text-ink-muted',
              )}
            >
              {/* E3: a late step is the late symbol plus its words in the late tone, as the
                  onboarding cell prints it; the circle itself no longer carries a warning edge. */}
              {state.status === 'late' && <StatusSymbol status="late" className="mr-0.5" />}
              {sentence}
            </span>
          )
        )}
      </span>
    </li>
  );
}

/**
 * A checkpoint's state as the one step mark's state. The record page knows no actor per step
 * (that map is the onboarding configuration's, `app/fulfillment/shared/stepActors.ts`), so a late
 * step is the plain late state; both late states draw the same ring at the row size.
 */
function stepMarkStateOf(state: CheckpointState): StepMarkState {
  switch (state.status) {
    case 'done':
      return 'done';
    case 'waived':
      return 'waived';
    case 'blocked':
      return 'blocked';
    case 'late':
      return 'late-us';
    case 'due':
      return state.days === 0 ? 'due-today' : 'open';
  }
}

/**
 * THE TWO THINGS A DONE ROW OFFERS, in the shape each viewport wants (D4).
 *
 * A popover on a desktop and a bottom sheet at 375, which is `Popover`'s own instruction:
 * "for anything a phone user must READ or FILL IN, prefer a BottomSheet". `Clear` carries a
 * subline, because "Clear" alone does not say that it also hands the field back to whatever
 * feeder fills it - and that consequence is the whole reason the item is not just a delete.
 */
function SetOptions({
  open,
  onOpenChange,
  isDesktop,
  label,
  circle,
  onChangeDate,
  onClear,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly isDesktop: boolean;
  readonly label: string;
  readonly circle: (onClick?: (() => void) | undefined) => React.ReactNode;
  readonly onChangeDate: () => void;
  readonly onClear: () => void;
}) {
  const items = (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onChangeDate}
        className="pressable min-h-(--touch-target-min) rounded-control px-2 text-left text-sm text-ink hover:bg-surface-hover"
      >
        Change date…
      </button>
      <button
        type="button"
        onClick={onClear}
        className="pressable min-h-(--touch-target-min) rounded-control px-2 py-1.5 text-left hover:bg-surface-hover"
      >
        <span className="block text-sm text-ink">Clear</span>
        <span className="block text-2xs text-ink-muted">
          Removes the date so an automated writer can fill it again.
        </span>
      </button>
    </div>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={onOpenChange} align="start" trigger={circle()}>
        {items}
      </Popover>
    );
  }

  return (
    <>
      {circle(() => onOpenChange(true))}
      <BottomSheet open={open} onOpenChange={onOpenChange} title={label}>
        {items}
      </BottomSheet>
    </>
  );
}

/** The label of the checkpoint this one waits for, for the tooltip on a blocked circle. */
function blockerOf(field: FieldMetadata, fields: readonly FieldMetadata[]): string | null {
  const blockedBy = field.options?.checkpoint?.blockedByFieldId;
  if (blockedBy === undefined) return null;
  return fields.find((candidate) => candidate.id === blockedBy)?.label ?? null;
}

/** True once `active` has been true for `delayMs`, and false again the instant it stops. */
function useDelayed(active: boolean, delayMs: number): boolean {
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (!active) {
      setLate(false);
      return;
    }
    const timer = setTimeout(() => setLate(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
  return late;
}
